import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Pagination,
  CircularProgress,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { formatDate } from '../../utils/formatDate';

const GRID_COLUMNS = {
  xs: '1fr',
  sm: '1.5fr 2fr 1fr',
  md: '1.5fr 2fr 0.8fr 0.8fr 0.7fr 1fr 0.7fr',
};

const Clientes = () => {
  const navigate = useNavigate();

  // State
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
  });
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    customer_type: '',
    billing_type: '',
  });
  const [sortBy, setSortBy] = useState('creation_date');
  const [sortOrder, setSortOrder] = useState('desc');

  const debounceTimerRef = useRef(null);
  const limit = 15;

  // Fetch customers
  const fetchCustomers = useCallback(async (page = 1, searchTerm = search) => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page,
        limit,
        sortBy,
        sortOrder,
      };

      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }
      if (filters.status !== 'all') {
        params.status = filters.status;
      }
      if (filters.customer_type) {
        params.customer_type = filters.customer_type;
      }
      if (filters.billing_type) {
        params.billing_type = filters.billing_type;
      }

      const response = await api.get('/admin/customers', { params });
      const { data, pagination: paginationData } = response.data;

      setCustomers(data || []);
      setPagination({
        currentPage: paginationData?.currentPage || 1,
        totalPages: paginationData?.totalPages || 1,
        totalItems: paginationData?.totalItems || 0,
      });
    } catch (err) {
      console.error('Error fetching customers:', err);
      setError('Error al cargar los clientes. Inténtalo de nuevo.');
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  }, [search, filters, sortBy, sortOrder]);

  // Initial fetch and refetch on filter/sort changes
  useEffect(() => {
    fetchCustomers(1, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, sortBy, sortOrder]);

  // Debounced search
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      fetchCustomers(1, value);
    }, 500);
  };

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Page change
  const handlePageChange = (_event, newPage) => {
    fetchCustomers(newPage, search);
  };

  // Filter changes
  const handleFilterChange = (field) => (e) => {
    setFilters((prev) => ({ ...prev, [field]: e.target.value }));
  };

  // Sort handler
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  // CSV Export
  const exportToCSV = (data) => {
    const headers = [
      'Nombre',
      'Email',
      'Tipo',
      'Billing',
      'Servicios',
      'Fecha registro',
      'Estado',
    ];
    const rows = data.map((c) => [
      `"${(c.customer_name || '').replace(/"/g, '""')}"`,
      `"${(c.customer_email || '').replace(/"/g, '""')}"`,
      c.customer_type || '',
      c.billing_type || '',
      c.services_count ?? 0,
      formatDate(c.creation_date),
      c.status ? 'Activo' : 'Inactivo',
    ]);
    const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `clientes_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const response = await api.get('/admin/customers/export');
      const data = response.data.data || response.data;
      exportToCSV(data);
    } catch (err) {
      console.error('Error exporting customers:', err);
      setError('Error al exportar los clientes.');
    } finally {
      setExporting(false);
    }
  };

  // Navigate to customer detail
  const handleRowClick = (customerId) => {
    navigate(`/admin/clientes/${customerId}`);
  };

  // Sort columns config
  const sortableColumns = [
    { id: 'customer_name', label: 'Nombre' },
    { id: 'customer_email', label: 'Email' },
    { id: 'customer_type', label: 'Tipo' },
    { id: 'billing_type', label: 'Billing' },
    { id: 'services_count', label: 'Servicios' },
    { id: 'creation_date', label: 'Fecha registro' },
    { id: 'status', label: 'Estado' },
  ];

  // Sort icon
  const SortIcon = ({ columnId }) => {
    if (sortBy !== columnId) return null;
    return sortOrder === 'asc'
      ? <ArrowUpwardIcon sx={{ fontSize: 14, ml: 0.5, color: '#0F52BD' }} />
      : <ArrowDownwardIcon sx={{ fontSize: 14, ml: 0.5, color: '#0F52BD' }} />;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: '100vw',
        px: { xs: 0, md: 2.5 },
        py: { xs: 0, md: 4 },
        pb: 0,
        mr: { xs: -6 },
        pr: { xs: 0 },
      }}
    >
      {/* Fixed Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          padding: { xs: '12px 16px', sm: '8px 16px', md: '12px 16px' },
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          backgroundColor: 'white',
          zIndex: 1000,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}
        >
          {/* Left: Title */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              ml: { xs: '50px', sm: '50px', md: 35.5 },
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontSize: { xs: '16px', sm: '18px', md: '20px', lg: '22px' },
                fontWeight: 'medium',
                color: '#000',
              }}
            >
              Clientes
              {!loading && (
                <Typography
                  component="span"
                  sx={{
                    fontSize: { xs: '14px', sm: '15px', md: '16px' },
                    fontWeight: 'normal',
                    color: '#666',
                    ml: 1,
                  }}
                >
                  ({pagination.totalItems})
                </Typography>
              )}
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                fontSize: { xs: '12px', sm: '15px', md: '14px' },
                color: '#666',
                mt: { xs: 0, md: 0.3 },
                display: { xs: 'none', sm: 'none', md: 'none', lg: 'block' },
              }}
            >
              Gestiona y consulta los clientes registrados en la plataforma
            </Typography>
          </Box>

          {/* Right: Action buttons */}
          <Box
            sx={{
              display: 'flex',
              gap: { xs: 1.5, sm: 2 },
              mr: { xs: 1.5, md: 5 },
              alignItems: 'center',
            }}
          >
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon sx={{ fontSize: { sm: '18px', md: '16px' } }} />}
              onClick={handleExport}
              disabled={exporting}
              sx={{
                textTransform: 'none',
                fontWeight: 'medium',
                color: '#0F52BD',
                borderColor: '#0F52BD',
                fontSize: { sm: '12px', md: '12px' },
                px: { sm: 1.5, md: 1.5 },
                py: { sm: 0.8, md: 0.8 },
                borderRadius: '8px',
                borderWidth: '2px',
                display: { xs: 'none', sm: 'flex' },
              }}
            >
              {exporting ? 'Exportando...' : 'Exportar CSV'}
            </Button>

            <Button
              variant="contained"
              startIcon={<AddIcon sx={{ fontSize: { sm: '18px', md: '16px' } }} />}
              onClick={() => navigate('/admin/clientes/nuevo')}
              sx={{
                textTransform: 'none',
                fontWeight: 'medium',
                backgroundColor: '#0F52BD',
                fontSize: { sm: '12px', md: '12px' },
                px: { sm: 1.5, md: 1.5 },
                py: { sm: 0.8, md: 0.8 },
                borderRadius: '8px',
                '&:hover': { backgroundColor: '#0a3d8f' },
                display: { xs: 'none', sm: 'flex' },
              }}
            >
              Nuevo Cliente
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Spacer for fixed header */}
      <Box
        sx={{
          height: { xs: '65px', sm: '70px', md: '100px', lg: '90px' },
          '@media (max-width: 600px) and (min-resolution: 1.25dppx)': {
            height: '65px',
          },
          '@media (max-width: 600px) and (min-resolution: 1.5dppx)': {
            height: '90px',
          },
          '@media (min-width: 600px) and (min-resolution: 1.25dppx)': {
            height: '75px',
          },
          '@media (min-width: 900px) and (min-resolution: 1.25dppx)': {
            height: '95px',
          },
        }}
      />

      {/* Main content */}
      <Box sx={{ ml: { xs: -6, md: 0 } }}>
        <Box sx={{ paddingLeft: { xs: 6, md: 2 }, paddingRight: { xs: 0, md: 4 } }}>
          {/* Error state */}
          {error && (
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ color: '#c62828', fontSize: '14px' }}>
                {error}
              </Typography>
            </Box>
          )}

          {/* Loading state */}
          {loading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '400px',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <CircularProgress size={40} sx={{ color: '#0F52BD' }} />
              <Typography variant="body2" sx={{ color: '#666' }}>
                Cargando clientes...
              </Typography>
            </Box>
          ) : customers.length === 0 ? (
            /* Empty state */
            <Paper
              elevation={0}
              sx={{
                p: 6,
                textAlign: 'center',
                boxShadow: '0px 0px 10px 2px rgba(98, 113, 137, 0.46)',
                borderRadius: 2.5,
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{ color: '#000', fontWeight: 'medium' }}
              >
                No se encontraron clientes
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: '#666', mb: 3 }}
              >
                {search || filters.status !== 'all' || filters.customer_type || filters.billing_type
                  ? 'No hay clientes que coincidan con los filtros aplicados.'
                  : 'Aún no hay clientes registrados en la plataforma.'}
              </Typography>
              {!search && filters.status === 'all' && !filters.customer_type && !filters.billing_type && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/admin/clientes/nuevo')}
                  sx={{
                    backgroundColor: '#0F52BD',
                    '&:hover': { backgroundColor: '#0a3d8f' },
                    textTransform: 'none',
                    fontWeight: 'medium',
                    px: 3,
                    py: 1.5,
                    borderRadius: '8px',
                  }}
                >
                  Crear Primer Cliente
                </Button>
              )}
            </Paper>
          ) : (
            /* Data Card */
            <Paper
              elevation={0}
              sx={{
                pt: 4,
                pl: 4,
                pr: 3,
                pb: 3,
                position: 'relative',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
                boxShadow: '0px 0px 10px 2px rgba(98, 113, 137, 0.46)',
                borderRadius: 2.5,
                overflow: 'hidden',
              }}
            >
              {/* Card header: Icon + Title | Search */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PeopleOutlineIcon sx={{ fontSize: 28, mr: 1, color: '#000' }} />
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{ fontSize: '18px', color: '#000' }}
                  >
                    Clientes
                  </Typography>
                </Box>

                <TextField
                  size="small"
                  placeholder="Buscar por nombre, email..."
                  value={search}
                  onChange={handleSearchChange}
                  autoComplete="off"
                  sx={{
                    width: { xs: '140px', sm: '220px', md: '180px', lg: '270px' },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      fontSize: '14px',
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <SearchIcon sx={{ color: '#999', mr: 1 }} />
                    ),
                  }}
                />
              </Box>

              {/* Filters row */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  mb: 3,
                  flexWrap: 'wrap',
                }}
              >
                <FormControl size="small" sx={{ minWidth: 160, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
                  <InputLabel>Tipo de cliente</InputLabel>
                  <Select
                    value={filters.customer_type}
                    onChange={handleFilterChange('customer_type')}
                    label="Tipo de cliente"
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="Business">Business</MenuItem>
                    <MenuItem value="Consumer">Consumer</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 140, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
                  <InputLabel>Billing</InputLabel>
                  <Select
                    value={filters.billing_type}
                    onChange={handleFilterChange('billing_type')}
                    label="Billing"
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="Stripe">Stripe</MenuItem>
                    <MenuItem value="AWS">AWS</MenuItem>
                    <MenuItem value="Big">Big</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 140, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
                  <InputLabel>Estado</InputLabel>
                  <Select
                    value={filters.status}
                    onChange={handleFilterChange('status')}
                    label="Estado"
                  >
                    <MenuItem value="all">Todos</MenuItem>
                    <MenuItem value="active">Activos</MenuItem>
                    <MenuItem value="inactive">Inactivos</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Grid data list */}
              <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
                {/* Grid Headers */}
                <Box
                  sx={{
                    display: { xs: 'none', md: 'grid' },
                    gridTemplateColumns: GRID_COLUMNS.md,
                    gap: 2,
                    py: 1,
                    borderBottom: '2px solid #E0E0E0',
                    mb: 1,
                    alignItems: 'center',
                  }}
                >
                  {sortableColumns.map((col) => (
                    <Box
                      key={col.id}
                      onClick={() => handleSort(col.id)}
                      sx={{
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        userSelect: 'none',
                        '&:hover': { opacity: 0.7 },
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 'bold',
                          fontSize: '14px',
                          color: sortBy === col.id ? '#0F52BD' : '#000',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {col.label}
                      </Typography>
                      <SortIcon columnId={col.id} />
                    </Box>
                  ))}
                </Box>

                {/* Grid Rows */}
                <Stack spacing={0}>
                  {customers.map((customer) => (
                    <Box
                      key={customer.customer_id}
                      onClick={() => handleRowClick(customer.customer_id)}
                      sx={{
                        display: { xs: 'block', sm: 'grid' },
                        gridTemplateColumns: { sm: GRID_COLUMNS.sm, md: GRID_COLUMNS.md },
                        gap: 2,
                        py: 2,
                        alignItems: 'center',
                        borderBottom: '1px solid #E0E0E0',
                        cursor: 'pointer',
                        transition: 'background-color 0.15s ease',
                        '&:hover': { backgroundColor: '#f5f8ff' },
                        '&:last-child': { borderBottom: 'none' },
                        px: { xs: 0, sm: 0 },
                      }}
                    >
                      {/* Vista movil - stacked */}
                      <Box
                        sx={{
                          display: { xs: 'block', sm: 'none' },
                          p: 2,
                          border: '1px solid #F0F0F0',
                          borderRadius: '8px',
                          backgroundColor: '#FAFAFA',
                        }}
                      >
                        <Typography sx={{ fontWeight: 'bold', fontSize: '16px', mb: 0.5 }}>
                          {customer.customer_name || '-'}
                        </Typography>
                        <Typography sx={{ fontSize: '14px', color: '#666', mb: 1 }}>
                          {customer.customer_email || '-'}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                          <Chip
                            label={customer.customer_type || '-'}
                            size="small"
                            sx={{ fontSize: '12px', backgroundColor: '#f5f5f5' }}
                          />
                          <Chip
                            label={customer.billing_type || '-'}
                            size="small"
                            sx={{ fontSize: '12px', backgroundColor: '#f5f5f5' }}
                          />
                          <Chip
                            label={customer.status ? 'Activo' : 'Inactivo'}
                            size="small"
                            sx={{
                              backgroundColor: customer.status ? '#e8f5e9' : '#ffebee',
                              color: customer.status ? '#2e7d32' : '#c62828',
                              border: `1px solid ${customer.status ? '#4caf50' : '#f44336'}`,
                              fontWeight: 'medium',
                              fontSize: '12px',
                            }}
                          />
                        </Box>
                      </Box>

                      {/* Vista tablet - 3 columnas */}
                      <Box sx={{ display: { xs: 'none', sm: 'flex', md: 'none' }, alignItems: 'center' }}>
                        <Typography sx={{ fontWeight: 'medium', fontSize: '14px' }}>
                          {customer.customer_name || '-'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: { xs: 'none', sm: 'block', md: 'none' } }}>
                        <Typography sx={{ fontSize: '14px', color: '#4F4F4F', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {customer.customer_email || '-'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: { xs: 'none', sm: 'flex', md: 'none' }, justifyContent: 'flex-end' }}>
                        <Chip
                          label={customer.status ? 'Activo' : 'Inactivo'}
                          size="small"
                          sx={{
                            backgroundColor: customer.status ? '#e8f5e9' : '#ffebee',
                            color: customer.status ? '#2e7d32' : '#c62828',
                            border: `1px solid ${customer.status ? '#4caf50' : '#f44336'}`,
                            fontWeight: 'medium',
                            fontSize: '12px',
                          }}
                        />
                      </Box>

                      {/* Vista desktop - 7 columnas */}
                      <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
                        <Typography sx={{ fontWeight: 'medium', fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {customer.customer_name || '-'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: { xs: 'none', md: 'block' }, overflow: 'hidden' }}>
                        <Typography sx={{ fontSize: '14px', color: '#4F4F4F', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {customer.customer_email || '-'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                        <Typography sx={{ fontSize: '14px', color: '#4F4F4F' }}>
                          {customer.customer_type || '-'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                        <Typography sx={{ fontSize: '14px', color: '#4F4F4F' }}>
                          {customer.billing_type || '-'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: { xs: 'none', md: 'block' }, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '14px', color: '#4F4F4F' }}>
                          {customer.services_count ?? 0}
                        </Typography>
                      </Box>
                      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                        <Typography sx={{ fontSize: '14px', color: '#666' }}>
                          {formatDate(customer.creation_date)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                        <Chip
                          label={customer.status ? 'Activo' : 'Inactivo'}
                          size="small"
                          sx={{
                            backgroundColor: customer.status ? '#e8f5e9' : '#ffebee',
                            color: customer.status ? '#2e7d32' : '#c62828',
                            border: `1px solid ${customer.status ? '#4caf50' : '#f44336'}`,
                            fontWeight: 'medium',
                            fontSize: '12px',
                          }}
                        />
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Box>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pt: 2,
                    mt: 2,
                    borderTop: '1px solid #E0E0E0',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 2, sm: 0 },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#666',
                      fontSize: '14px',
                      textAlign: { xs: 'center', sm: 'left' },
                    }}
                  >
                    {`Mostrando ${(pagination.currentPage - 1) * limit + 1} - ${Math.min(
                      pagination.currentPage * limit,
                      pagination.totalItems
                    )} de ${pagination.totalItems} clientes`}
                  </Typography>
                  <Pagination
                    count={pagination.totalPages}
                    page={pagination.currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    shape="rounded"
                    size="small"
                    sx={{
                      '& .MuiPaginationItem-root': {
                        fontSize: '14px',
                        minWidth: '32px',
                        height: '32px',
                      },
                      '& .Mui-selected': {
                        backgroundColor: '#0F52BD !important',
                        color: 'white',
                      },
                      '& .MuiPaginationItem-root:hover': {
                        backgroundColor: '#E3F2FD',
                      },
                      '& .MuiPaginationItem-previousNext': {
                        border: '1px solid #E0E0E0',
                      },
                    }}
                  />
                </Box>
              )}
            </Paper>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Clientes;
